import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Search, Filter, MoreHorizontal, Plus, X, Trash, Edit, ShieldAlert } from 'lucide-react';
import { UserProfile } from '../../types';

export const Admins: React.FC = () => {
  const { user } = useOutletContext<{ user: UserProfile }>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [actionMenuOpen, setActionMenuOpen] = useState<number | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  
  const [roleFilter, setRoleFilter] = useState('All');
  const [dateFilter, setDateFilter] = useState('');

  const [formData, setFormData] = useState({
      firstName: '', lastName: '', email: '', phoneNumber: '', responsibility: '', yearOfStudy: ''
  });

  const [admins, setAdmins] = useState([
        { id: 1, name: 'John Snow', role: 'Admin', email: 'john@speedlink.com', date: '2025-10-01' },
        { id: 2, name: 'Arya Stark', role: 'Super Admin', email: 'arya@speedlink.com', date: '2025-09-15' },
        { id: 3, name: 'Tyrion L.', role: 'Editor', email: 'tyrion@speedlink.com', date: '2025-11-20' },
        { id: 4, name: 'Sansa Stark', role: 'Admin', email: 'sansa@speedlink.com', date: '2025-10-05' },
        { id: 5, name: 'Bran Stark', role: 'Viewer', email: 'bran@speedlink.com', date: '2025-12-01' },
        { id: 6, name: 'Daenerys T.', role: 'Admin', email: 'dany@speedlink.com', date: '2025-10-02' },
    ]);

  // Access Control Check
  if (user.role !== 'SuperAdmin') {
      return (
          <div className="flex flex-col items-center justify-center h-[60vh] text-center space-y-4 animate-in fade-in duration-500">
              <div className="w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                  <ShieldAlert className="w-10 h-10 text-red-600 dark:text-red-400" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Access Denied</h2>
              <p className="text-gray-500 dark:text-gray-400 max-w-md">
                  You do not have permission to view this page. This section is restricted to Super Administrators only.
              </p>
          </div>
      );
  }

  const filteredAdmins = admins.filter(admin => {
      const matchesSearch = admin.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            admin.email.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesRole = roleFilter === 'All' || admin.role === roleFilter;
      const matchesDate = dateFilter === '' || admin.date === dateFilter;

      return matchesSearch && matchesRole && matchesDate;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredAdmins.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredAdmins.length / itemsPerPage);

  const handlePageChange = (newPage: number) => {
      if (newPage >= 1 && newPage <= totalPages) setCurrentPage(newPage);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleAddAdmin = () => {
      if (!formData.firstName || !formData.email) {
          alert("Please fill required fields");
          return;
      }
      const newAdmin = {
          id: Date.now(),
          name: `${formData.firstName} ${formData.lastName}`,
          role: formData.responsibility || 'Admin',
          email: formData.email,
          date: new Date().toISOString().split('T')[0]
      };
      setAdmins([newAdmin, ...admins]);
      setIsModalOpen(false);
      setFormData({ firstName: '', lastName: '', email: '', phoneNumber: '', responsibility: '', yearOfStudy: '' });
      alert("Admin added successfully!");
  };

  const handleDelete = (id: number, e: React.MouseEvent) => {
      e.stopPropagation();
      const adminToDelete = admins.find(a => a.id === id);
      
      if (adminToDelete?.role === 'Super Admin') {
          alert("Permission Denied: You cannot delete a Super Admin.");
          setActionMenuOpen(null);
          return;
      }

      if (confirm("Are you sure you want to remove this admin?")) {
          setAdmins(prev => prev.filter(a => a.id !== id));
          setActionMenuOpen(null);
      }
  };

  const toggleActionMenu = (id: number) => { setActionMenuOpen(actionMenuOpen === id ? null : id); };
  
  useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (!(event.target as Element).closest('.action-menu-container')) setActionMenuOpen(null);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div><h2 className="text-2xl font-bold text-gray-900 dark:text-white">Admins</h2><p className="text-sm text-gray-600 dark:text-gray-400 mt-1 font-medium">Manage admin access and permissions.</p></div>
        <div className="flex items-center gap-3">
            <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 w-4 h-4" />
                <input 
                  type="text" 
                  placeholder="Search for admin..." 
                  value={searchQuery} 
                  onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }} 
                  className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm w-full md:w-64 focus:ring-primary focus:border-primary text-gray-900 dark:text-white bg-white dark:bg-gray-700 placeholder-gray-500 dark:placeholder-gray-400 transition-colors" 
                />
            </div>
            <button onClick={() => setIsModalOpen(true)} className="bg-darkblue text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-indigo-900 transition-colors flex items-center gap-2 whitespace-nowrap shadow-sm"><Plus className="w-4 h-4" /> Add Admin</button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col min-h-[400px] transition-colors">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-800/50">
            <h3 className="font-bold text-gray-800 dark:text-white">All Admins</h3>
            <div className="flex gap-2">
                <button 
                    onClick={() => setShowFilters(!showFilters)}
                    className={`flex items-center gap-2 px-3 py-1.5 border rounded-md text-sm font-medium transition-colors ${showFilters ? 'bg-primary text-white border-primary' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                >
                    <Filter className="w-4 h-4" /> Filter
                </button>
            </div>
        </div>

        {showFilters && (
            <div className="p-4 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 grid grid-cols-1 sm:grid-cols-2 gap-4 animate-in fade-in slide-in-from-top-2 duration-200">
                <select 
                    value={roleFilter}
                    onChange={(e) => { setRoleFilter(e.target.value); setCurrentPage(1); }}
                    className="border border-gray-300 dark:border-gray-600 rounded-md p-2 text-sm focus:ring-primary focus:border-primary text-gray-800 dark:text-white bg-white dark:bg-gray-700 font-medium"
                >
                    <option value="All">Role: All</option>
                    <option value="Admin">Admin</option>
                    <option value="Super Admin">Super Admin</option>
                    <option value="Editor">Editor</option>
                    <option value="Viewer">Viewer</option>
                </select>
                <input 
                    type="date" 
                    value={dateFilter}
                    onChange={(e) => { setDateFilter(e.target.value); setCurrentPage(1); }}
                    className="border border-gray-300 dark:border-gray-600 rounded-md p-2 text-sm focus:ring-primary focus:border-primary text-gray-800 dark:text-white bg-white dark:bg-gray-700 font-medium" 
                    placeholder="Filter by Date"
                />
            </div>
        )}

        <div className="overflow-x-auto flex-grow">
            <table className="w-full text-left text-sm text-gray-700 dark:text-gray-300">
                <thead className="bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 uppercase text-xs font-bold">
                    <tr><th className="px-6 py-3 w-4"><input type="checkbox" className="rounded border-gray-400 text-primary focus:ring-primary bg-white" /></th><th className="px-6 py-3">Name</th><th className="px-6 py-3">Role</th><th className="px-6 py-3">Email</th><th className="px-6 py-3">Date Added</th><th className="px-6 py-3">Action</th></tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {currentItems.length > 0 ? (
                        currentItems.map((admin) => (
                        <tr key={admin.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 relative transition-colors">
                            <td className="px-6 py-4"><input type="checkbox" className="rounded border-gray-400 text-primary focus:ring-primary bg-white" /></td>
                            <td className="px-6 py-4 font-semibold text-gray-900 dark:text-white">{admin.name}</td>
                            <td className="px-6 py-4 text-gray-800 dark:text-gray-300 font-medium">{admin.role}</td>
                            <td className="px-6 py-4 text-gray-700 dark:text-gray-300">{admin.email}</td>
                            <td className="px-6 py-4 text-gray-800 dark:text-gray-300">{admin.date}</td>
                            <td className="px-6 py-4 action-menu-container relative">
                                <button onClick={(e) => { e.stopPropagation(); toggleActionMenu(admin.id); }} className="text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-white p-1"><MoreHorizontal className="w-5 h-5" /></button>
                                {actionMenuOpen === admin.id && (
                                    <div className="absolute right-8 top-0 mt-2 w-32 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-20 animate-in fade-in zoom-in duration-200">
                                        <div className="py-1">
                                            <button onClick={() => { setIsModalOpen(true); setActionMenuOpen(null); }} className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 w-full text-left font-medium"><Edit className="w-4 h-4" /> Edit</button>
                                            <button 
                                                onClick={(e) => handleDelete(admin.id, e)} 
                                                className="flex items-center gap-2 px-4 py-2 text-sm text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 w-full text-left font-medium"
                                            >
                                                <Trash className="w-4 h-4" /> Delete
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </td>
                        </tr>
                    ))) : ( <tr><td colSpan={6} className="px-6 py-8 text-center text-gray-600 dark:text-gray-400 font-medium">No admins found matching criteria.</td></tr> )}
                </tbody>
            </table>
        </div>
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-800/50">
            <span className="text-xs text-gray-700 dark:text-gray-300 font-medium">Showing {filteredAdmins.length > 0 ? indexOfFirstItem + 1 : 0}-{Math.min(indexOfLastItem, filteredAdmins.length)} of {filteredAdmins.length} admins</span>
            <div className="flex gap-2">
                <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-xs hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 text-gray-800 dark:text-gray-200 font-medium transition-colors">Previous</button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button key={page} onClick={() => handlePageChange(page)} className={`px-3 py-1 border rounded text-xs font-medium transition-colors ${currentPage === page ? 'bg-primary text-white border-primary' : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600'}`}>{page}</button>
                ))}
                <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-xs hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 text-gray-800 dark:text-gray-200 font-medium transition-colors">Next</button>
            </div>
        </div>
      </div>

       {isModalOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
              <div className="bg-white dark:bg-gray-800 rounded-xl max-w-4xl w-full shadow-2xl flex flex-col max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700">
                  <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center sticky top-0 bg-white dark:bg-gray-800 z-10">
                      <div className="flex items-center gap-2"><h3 className="text-xl font-bold text-gray-900 dark:text-white">Add Admin</h3></div>
                      <button onClick={() => setIsModalOpen(false)} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"><X className="w-5 h-5" /></button>
                  </div>
                  <div className="p-6">
                      <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-6">Input Admin Details</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {['First Name', 'Last Name', 'Email', 'Phone Number', 'Responsibility', 'Year of Study'].map(label => {
                               const name = label.toLowerCase().replace(/\s/g, '').replace('ofstudy', 'OfStudy').replace('firstname', 'firstName').replace('lastname', 'lastName').replace('phonenumber', 'phoneNumber').replace('yearofstudy', 'yearOfStudy');
                               return (
                                <div key={label}>
                                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">{label} *</label>
                                    <input 
                                        type={label === 'Email' ? 'email' : 'text'}
                                        name={name} 
                                        //@ts-ignore
                                        value={formData[name]} 
                                        onChange={handleInputChange} 
                                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 focus:ring-primary focus:border-primary text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700 placeholder-gray-500 dark:placeholder-gray-400" 
                                        placeholder={`Enter ${label}`} 
                                    />
                                </div>
                               );
                          })}
                      </div>
                      <div className="mt-8"><button onClick={handleAddAdmin} className="w-fit bg-darkblue text-white px-8 py-3 rounded-lg text-sm font-bold hover:bg-indigo-900 transition-all shadow-md">Add Admin</button></div>
                  </div>
              </div>
          </div>
      )}
    </div>
  );
};